import React, { useCallback, useContext, useMemo } from 'react';
import { DateRangePicker, Range } from 'react-date-range';
// react-date-range's onChange callback receives a RangeKeyDict which is
// { [key: string]: Range }. Different @types/react-date-range versions
// have used either `OnChangeProps` or `RangeKeyDict` for this name —
// keep the import surface narrow by defining the shape inline so the
// build doesn't drift with @types resolution.
type OnChangeProps = { [key: string]: Range };
import { useIntl } from 'react-intl';
import { ThemeContext } from 'styled-components';
import { IconArrowForward, IconCalendar, IconChevronDown } from '../../../../assets/svg/icons';
import useToggle from '../../../../hooks/useToggle';
import { DatepickerPosition } from './enums';
import {
  StyledDatepicker,
  StyledDatepickerButton,
  StyledDatepickerDecor,
} from './styles';

import { DatepickerProps } from './types';
import { formatDateShort, getStaticRanges } from './utils';
import { BRAND_GREEN } from '../../../../theme/brandColors';

const Datepicker: React.FC<DatepickerProps> = ({
  onChange,
  value,
  position = DatepickerPosition.Left,
  disabled,
}) => {
  const intl = useIntl();
  const theme = useContext(ThemeContext);
  const [isOpen, { toggle, registerContainerRef }] = useToggle();
  const handleOnChange = useCallback(
    (item: OnChangeProps) => {
      if ('selection' in item) {
        onChange({
          startDate: item.selection.startDate,
          endDate: item.selection.endDate,
        });
      }
    },
    [onChange]
  );

  const staticRanges = useMemo(() => getStaticRanges(intl), [intl]);

  return (
    <StyledDatepicker {...{ position }} ref={registerContainerRef}>
      <StyledDatepickerButton onClick={toggle} {...{ disabled }} aria-expanded={isOpen}>
        <span className='fc-icon'><IconCalendar /></span>
        <span className='fc-label'>
          {value.startDate && formatDateShort(value.startDate)}
          {' '}
          <StyledDatepickerDecor as='span'>
            <IconArrowForward />
          </StyledDatepickerDecor>
          {' '}
          {value.endDate && formatDateShort(value.endDate)}
        </span>
        <span className='fc-chevron'><IconChevronDown /></span>
      </StyledDatepickerButton>
      {isOpen && (
        <DateRangePicker
          ranges={[{ ...value, key: 'selection' }]}
          staticRanges={staticRanges}
          onChange={handleOnChange}
          direction='horizontal'
          months={2}
          showDateDisplay={false}
          moveRangeOnFirstSelection
          showPreview={false}
          className='datepicker'
          rangeColors={[BRAND_GREEN]}
          monthDisplayFormat='MMMM yyyy'
          weekdayDisplayFormat='EEEEEE'
          weekStartsOn={1}
        />
      )}
    </StyledDatepicker>
  );
};

export default Datepicker;
